import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/auth';

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [loading, setLoading] = useState(false);

  // Initialize language based on user preference or localStorage
  useEffect(() => {
    const initializeLanguage = async () => {
      const user = authService.getCurrentUser();
      
      if (user && user.language) {
        // User is authenticated and has a language preference
        setCurrentLanguage(user.language);
        i18n.changeLanguage(user.language);
      } else {
        // Fall back to localStorage or default
        const savedLanguage = localStorage.getItem('language') || 'en';
        setCurrentLanguage(savedLanguage);
        i18n.changeLanguage(savedLanguage);
      }
    };

    initializeLanguage();
  }, [i18n]);

  // Update language preference for authenticated user
  const updateLanguage = async (language) => {
    setLoading(true);
    
    try {
      const user = authService.getCurrentUser();
      
      if (user) {
        // User is authenticated - update in database
        await authService.updateLanguage(language);
      } else {
        // User not authenticated - just update localStorage
        localStorage.setItem('language', language);
      }
      
      // Update local state and i18n
      setCurrentLanguage(language);
      await i18n.changeLanguage(language);
      
    } catch (error) {
      console.error('Failed to update language:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Sync language when user logs in
  const syncUserLanguage = (user) => {
    if (user && user.language && user.language !== currentLanguage) {
      setCurrentLanguage(user.language);
      i18n.changeLanguage(user.language);
    }
  };

  // Reset to localStorage language when user logs out
  const resetToLocalLanguage = () => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
    i18n.changeLanguage(savedLanguage);
  };

  // Check and sync user language from server (for when admin changes current user's language)
  const checkAndSyncUserLanguage = async () => {
    try {
      const user = authService.getCurrentUser();
      if (!user) return;

      // Fetch the latest user data from server
      const updatedUser = await authService.fetchCurrentUser();
      
      // Check if language has changed
      if (updatedUser.language && updatedUser.language !== currentLanguage) {
        // Update local state and i18n immediately
        setCurrentLanguage(updatedUser.language);
        await i18n.changeLanguage(updatedUser.language);
        
        console.log(`Language automatically synced to: ${updatedUser.language}`);
      }
    } catch (error) {
      console.error('Failed to check and sync user language:', error);
    }
  };

  const value = {
    currentLanguage,
    updateLanguage,
    loading,
    syncUserLanguage,
    resetToLocalLanguage,
    checkAndSyncUserLanguage
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;